import React from "react";
import axios from "axios";
import { FileSpreadsheet, RefreshCw, Trash2 } from "lucide-react";
import { Select } from "../../form/select";
import { getGoogleAccountToken } from "../../auth/method";
import { webURL } from "../../../services/API";
import { selectDataType } from "../../../services/types";
import { GlobalGoogleSpreadsheet } from "./spreadsheetUtils";

type Props = {
   project: projectType;
   googleConnectionId: string;
   onSaved: (spreadsheet: GlobalGoogleSpreadsheet | null) => void;
};

export function GlobalGoogleSpreadsheetPicker({ project, googleConnectionId, onSaved }: Props) {
   const [files, setFiles] = React.useState<selectDataType[]>([]);
   const [isLoading, setIsLoading] = React.useState(false);
   const [connection, setConnection] = React.useState<any>(null);

   React.useEffect(() => {
      if (!googleConnectionId) {
         setConnection(null);
         setFiles([]);
         return;
      }
      let cancelled = false;
      (async () => {
         try {
            const response = await axios.get(`${webURL}/connections?byName=google`);
            const found = (Array.isArray(response.data) ? response.data : [])
               .find((item: any) => item._id === googleConnectionId);
            if (!cancelled) setConnection(found ? { id: found._id, ...found.info, name: found.description } : null);
         } catch {
            if (!cancelled) setConnection(null);
         }
      })();
      return () => { cancelled = true; };
   }, [googleConnectionId]);

   const fetchDocuments = async () => {
      if (!connection) return;
      setIsLoading(true);
      try {
         const token = await getGoogleAccountToken(connection);
         const response = (await axios.get(`/project_management/spreadsheet/user_spreadsheets?token=${token}`)).data;
         setFiles((response.spreadsheets || []).map((file: any, index: number) => ({
            id: index + 1,
            refId: file.id,
            name: file.name,
            isActive: file.id === project.globalGoogleSpreadsheet?.spreadsheet_id,
         })));
      } catch {
         setFiles([]);
      }
      setIsLoading(false);
   };

   React.useEffect(() => {
      if (connection && project.globalGoogleSpreadsheet?.spreadsheet_id && !files.length) {
         fetchDocuments();
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [connection, project.globalGoogleSpreadsheet?.spreadsheet_id]);

   const handleSelectDoc = (selectId: number) => {
      const pool = files.length
         ? files
         : project.globalGoogleSpreadsheet?.spreadsheet_id
            ? [{
               id: 1,
               refId: project.globalGoogleSpreadsheet.spreadsheet_id,
               name: project.globalGoogleSpreadsheet.spreadsheet_name || project.globalGoogleSpreadsheet.spreadsheet_id,
            }]
            : [];
      const selected = pool.find(f => f.id === selectId);
      if (!selected?.refId) return;
      const update = { spreadsheet_id: selected.refId, spreadsheet_name: selected.name };
      if (files.length) setFiles(files.map(f => ({ ...f, isActive: f.id === selectId })));
      onSaved(update);
   };

   const handleClear = () => {
      setFiles([]);
      onSaved(null);
   };

   const documentItems: selectDataType[] = files.length
      ? files
      : project.globalGoogleSpreadsheet?.spreadsheet_id
         ? [{
            id: 1,
            refId: project.globalGoogleSpreadsheet.spreadsheet_id,
            name: project.globalGoogleSpreadsheet.spreadsheet_name || project.globalGoogleSpreadsheet.spreadsheet_id,
            isActive: true,
         }]
         : [];

   if (!googleConnectionId) {
      return (
         <div className="spreadsheetGlobal">
            <div className="subLabel">Default Spreadsheet</div>
            <p className="subHint">Select a Google connection above first.</p>
         </div>
      );
   }

   return (
      <div className="spreadsheetGlobal">
         <div className="subLabel">Default Spreadsheet (Document)</div>
         <p className="subHint">Applies to all Spreadsheet nodes. Each node keeps its own Sheet tab.</p>
         <div className="spreadsheetActions">
            <button className="btnFetch" onClick={fetchDocuments} disabled={isLoading || !connection}>
               <RefreshCw size={15} className={isLoading ? 'spinning' : ''} />
               {isLoading ? 'Fetching...' : 'Fetch Documents'}
            </button>
            {project.globalGoogleSpreadsheet?.spreadsheet_id ? (
               <button className="btnClear" onClick={handleClear} disabled={isLoading}>
                  <Trash2 size={14} />
                  Clear
               </button>
            ) : null}
         </div>
         <div className="documentField">
            <div className="documentLabel"><FileSpreadsheet size={15} /> Document</div>
            <Select
               data={documentItems}
               handleSelectItem={handleSelectDoc}
               isSmall={true}
               emptyMsg={isLoading ? 'Loading documents...' : 'Fetch documents to choose one'}
            />
         </div>
      </div>
   );
}
